import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Download, Wallet, CalendarCheck, Receipt, Briefcase, User2, Mail, ArrowUpRight,
  Phone, MapPin, CreditCard, Pencil, ShieldCheck, Crown, Star, BadgeCheck,
} from "lucide-react";
import Attendance from "@/pages/Attendance";
import EditProfileDialog from "@/components/EditProfileDialog";

/**
 * Profile — Iter 45.14 rebuild.
 *
 * Layout: an Apple-inspired glass card stack over a subtle purple/indigo
 * ambient background. Every section is a translucent card with
 * `backdrop-blur-xl` so the background gradient bleeds through. Every
 * existing behaviour (payslip download, embedded Attendance) is preserved.
 *
 * New this iter:
 *   • Role badges for TL / CM / Head of Content / Content Ops Manager.
 *   • "Edit Your Details" dialog with avatar upload + bank + contact +
 *     address. Bank details echo onto the Payroll sheet automatically.
 */
export default function Profile() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const nowMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(nowMonth);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/profile/me");
        setMe(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!month) return;
    (async () => {
      try {
        const r = await api.get(`/profile/payslip?month=${month}`);
        setPayslip(r.data);
      } catch (e) { setPayslip(null); }
    })();
  }, [month]);

  const canOpenPayroll = user && (
    ["super_admin", "admin", "content_manager", "team_lead"].includes(user.role) ||
    (user.departments || [user.department] || []).map((d) => (d || "").toLowerCase()).includes("hr")
  );

  const downloadPayslip = async () => {
    setDownloading(true);
    try {
      const bust = `&uid=${encodeURIComponent(me?.id || user?.id || "")}&t=${Date.now()}`;
      const r = await api.get(`/profile/payslip.pdf?month=${month}${bust}`, {
        responseType: "blob",
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CreateXMedia_Payslip_${month}_${(me?.name || "employee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Payslip downloaded");
    } catch (e) {
      let msg = null;
      try {
        const text = await e.response?.data?.text?.();
        if (text) { const obj = JSON.parse(text); msg = obj?.detail; }
      } catch { /* ignore */ }
      toast.error(msg || formatApiError(e) || "Payslip not available yet");
    } finally { setDownloading(false); }
  };

  const initials = useMemo(() => {
    const n = (me?.name || user?.name || "U").trim();
    return n.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  }, [me, user]);

  // Compute role badges. Pulls both the primary role AND any `positions[]`
  // secondary roles so a Team Lead who also has a Content Ops Manager
  // position gets both pills.
  const roleBadges = useMemo(() => {
    if (!me) return [];
    const out = [];
    const seen = new Set();
    const push = (b) => { if (!seen.has(b.label)) { seen.add(b.label); out.push(b); } };
    const primaryDept = (me.departments && me.departments[0]) || me.department || "";
    const positions = [
      { role: me.role, position: me.position, department: primaryDept },
      ...((me.positions || []).map((p) => ({ role: p.role, position: p.position, department: p.department }))),
    ];
    for (const p of positions) {
      if (p.role === "super_admin") push({ label: "Super Admin", cls: "bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-amber-300", icon: Crown });
      if (p.role === "admin")       push({ label: "Admin", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: ShieldCheck });
      if (p.role === "team_lead")   push({ label: "Team Leader", cls: "bg-sky-100 text-sky-700 border-sky-200", icon: Star });
      if (p.role === "content_manager") {
        if (p.position === "Content Ops Manager") push({ label: "Content Ops Manager", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: BadgeCheck });
        else push({ label: "Content Manager", cls: "bg-purple-100 text-purple-700 border-purple-200", icon: BadgeCheck });
      }
      if (p.role === "department_admin") {
        const dept = (p.department || "").toLowerCase();
        if (dept === "content" || p.position === "Head of Content") push({ label: "Head of Content", cls: "bg-violet-100 text-violet-700 border-violet-200", icon: BadgeCheck });
        else push({ label: `${(dept || "dept").charAt(0).toUpperCase() + (dept || "dept").slice(1)} Dept Admin`, cls: "bg-violet-100 text-violet-700 border-violet-200", icon: BadgeCheck });
      }
    }
    return out;
  }, [me]);

  if (!me) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="profile-loading">Loading profile…</div>;
  }

  const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const glass = "bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-purple-500/5";

  return (
    <div className="relative min-h-full" data-testid="profile-page">
      {/* Ambient background — gives the glass cards something to blur */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-20 left-10 h-[420px] w-[420px] rounded-full bg-purple-300/40 blur-3xl" />
        <div className="absolute top-1/3 right-0 h-[380px] w-[380px] rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[340px] w-[340px] rounded-full bg-fuchsia-300/25 blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-5 relative">
        {/* -------- Hero -------- */}
        <Card className={`${glass} p-6`}>
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative shrink-0">
              {me.avatar_url ? (
                <img
                  src={me.avatar_url}
                  alt={me.name}
                  className="h-24 w-24 rounded-2xl object-cover border-2 border-white/80 shadow-lg"
                  data-testid="profile-avatar-image"
                />
              ) : (
                <div
                  className="h-24 w-24 rounded-2xl bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-500 text-white flex items-center justify-center text-3xl font-bold border-2 border-white/80 shadow-lg"
                  data-testid="profile-avatar"
                >
                  {initials}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-purple-500/80">
                {me.department || "team"} team
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900 leading-tight" data-testid="profile-name">
                {me.name}
              </h1>
              <div className="mt-1.5 text-sm text-slate-500 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {me.email}
                {me.employee_id && (
                  <span className="ml-2 font-mono text-xs bg-slate-100 border border-slate-200 px-2 py-0.5 rounded" data-testid="profile-employee-id">
                    #{me.employee_id}
                  </span>
                )}
              </div>
              {/* Role badges */}
              {roleBadges.length > 0 && (
                <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="profile-role-badges">
                  {roleBadges.map((b) => {
                    const BIcon = b.icon;
                    return (
                      <span
                        key={b.label}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${b.cls}`}
                        data-testid={`profile-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <BIcon className="h-3 w-3" /> {b.label}
                      </span>
                    );
                  })}
                  {me.member_type === "intern" && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">
                      Intern
                    </span>
                  )}
                </div>
              )}
              {/* Reports-to */}
              {me.reports_to && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-500 bg-white/40 border border-white/50 px-2.5 py-1 rounded-full backdrop-blur-md">
                  <User2 className="h-3 w-3" /> Reports to <span className="font-semibold text-slate-700">{me.reports_to.name}</span>
                  <span className="text-slate-400">· {me.reports_to.designation}</span>
                </div>
              )}
            </div>
            <div className="shrink-0">
              <Button
                onClick={() => setEditOpen(true)}
                className="bg-white/70 backdrop-blur-md hover:bg-purple-600 hover:text-white text-slate-700 border border-white/70 shadow-sm"
                data-testid="edit-profile-btn"
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Edit Your Details
              </Button>
            </div>
          </div>
        </Card>

        {/* -------- Snapshot cards -------- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={`${glass} p-5`} data-testid="profile-salary">
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5 text-purple-500" /> {me.member_type === "intern" ? "Stipend" : "Salary"} · monthly
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-slate-900">{fmtINR(me.salary)}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Per-day wage · <span className="font-medium text-slate-700">{fmtINR(me.per_day_wage ?? (me.salary / 30))}</span>
            </div>
          </Card>

          <Card className={`${glass} p-5`} data-testid="profile-attendance-card">
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] flex items-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5 text-purple-500" /> This month ({me.attendance.month})
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <MiniStat value={me.attendance.total_working_days} label="Working" />
              <MiniStat value={me.attendance.total_leaves} label="Leaves" tone="rose" />
              <MiniStat value={me.attendance.paid_days} label="Paid" tone="emerald" />
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-600 flex-wrap">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 border border-sky-100"><span className="font-semibold">CL</span> {me.attendance.cl}</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 border border-rose-100"><span className="font-semibold">ML</span> {me.attendance.ml}</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100"><span className="font-semibold">Half</span> {me.attendance.half_days}</span>
            </div>
          </Card>

          <Card className={`${glass} p-5`} data-testid="profile-to-credit">
            <div className="text-[10px] uppercase font-bold text-emerald-600 tracking-[0.14em] flex items-center gap-1.5">
              <Wallet className="h-3.5 w-3.5" /> To credit this month
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-emerald-700">{fmtINR(me.attendance.to_credit)}</div>
            <div className="text-[11px] text-slate-500 mt-1">Live payroll compute · matches the Payroll sheet 1:1.</div>
          </Card>
        </div>

        {/* -------- Personal details row -------- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className={`${glass} p-5`} data-testid="profile-contact-card">
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-purple-500" /> Contact
            </div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <DetailRow label="Personal email"    value={me.personal_email}     empty="Add email" />
              <DetailRow label="Contact number"    value={me.contact_number}     empty="Add number" />
              <DetailRow label="Alternate"         value={me.alt_contact_number} empty="—" />
              <DetailRow label="Emergency"         value={me.emergency_contact}  empty="—" />
            </dl>
            <div className="mt-3 pt-3 border-t border-white/60">
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-purple-500" /> Current address
              </div>
              <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap" data-testid="profile-address">
                {me.current_address ? me.current_address : <span className="italic text-slate-400">No address yet — click "Edit Your Details" to add.</span>}
              </div>
            </div>
          </Card>

          <Card className={`${glass} p-5`} data-testid="profile-bank-card">
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] flex items-center gap-1.5">
              <CreditCard className="h-3.5 w-3.5 text-purple-500" /> Bank details
              <span className="ml-auto text-[9px] text-purple-500/70 normal-case tracking-normal font-medium">Syncs to Payroll</span>
            </div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <DetailRow label="Bank name"     value={me.bank_name}                empty="Add bank" />
              <DetailRow label="IFSC"          value={me.ifsc}                     empty="—" mono />
              <DetailRow label="Account No."   value={me.account_number}           empty="—" mono />
              <DetailRow label="UPI ID"        value={me.upi_id}                   empty="—" />
            </dl>
            {!(me.bank_name || me.ifsc || me.account_number || me.upi_id) && (
              <div className="mt-3 text-[11px] text-amber-800 bg-amber-50/70 border border-amber-200 rounded-lg px-3 py-2">
                Bank details help HR credit your monthly payout on time. Click <strong>Edit Your Details</strong> to add them.
              </div>
            )}
          </Card>
        </div>

        {/* -------- Payslip -------- */}
        <Card className={`${glass} p-6`} data-testid="profile-payslip-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 text-purple-600" /> Payslip
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                CreateX Media payslip · finalised on the last working day of every month.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-9 w-40 bg-white/60 backdrop-blur-md border-white/60"
                data-testid="payslip-month-picker"
              />
              <Button
                onClick={downloadPayslip}
                disabled={downloading || !payslip?.ready}
                className="bg-purple-600 hover:bg-purple-700 text-white h-9"
                data-testid="payslip-download-btn"
              >
                <Download className="h-4 w-4 mr-1.5" />
                {downloading ? "Downloading…" : "Download Payslip"}
              </Button>
              {canOpenPayroll && (
                <Button
                  variant="outline"
                  onClick={() => nav("/payroll")}
                  className="h-9 bg-white/60 backdrop-blur-md border-white/60"
                  data-testid="payslip-see-payroll"
                >
                  <Wallet className="h-4 w-4 mr-1.5" /> View Payroll <ArrowUpRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </div>

          {payslip && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <PaySumCell label="Total Working Days" value={payslip.total_working_days} />
              <PaySumCell label="Total Leaves"       value={payslip.total_leaves} />
              <PaySumCell label="Casual Leaves"      value={payslip.casual_leaves} />
              <PaySumCell label="Medical Leaves"     value={payslip.medical_leaves} />
              <PaySumCell label="Half Days"          value={payslip.half_days} />
              <PaySumCell label="Paid Days"          value={payslip.paid_days} />
              <PaySumCell label="Per-day Wage"       value={fmtINR(payslip.per_day_wage)} />
              <PaySumCell label="To Credit"          value={fmtINR(payslip.to_credit)} highlight />
            </div>
          )}
          {payslip && !payslip.ready && (
            <div
              className="mt-3 text-[11px] text-amber-800 bg-amber-50/80 border border-amber-200 rounded px-3 py-2 inline-block"
              data-testid="payslip-not-ready"
            >
              Payslip for <strong>{payslip.month_label}</strong> is finalised on the last working day. You can preview the numbers now — download unlocks then.
            </div>
          )}
        </Card>

        {/* -------- Attendance calendar -------- */}
        <Card className={`${glass} p-6`} data-testid="profile-attendance-section">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CalendarCheck className="h-5 w-5 text-purple-600" /> Attendance
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Your live attendance calendar — log ins/outs, applied leaves, and pending approvals.
              </p>
            </div>
          </div>
          <Attendance embedded />
        </Card>
      </div>

      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        me={me}
        onSaved={(next) => setMe(next)}
      />
    </div>
  );
}

function PaySumCell({ label, value, highlight }) {
  return (
    <div
      className={`p-3 rounded-lg border ${highlight ? "border-purple-300 bg-gradient-to-br from-purple-50 to-fuchsia-50" : "border-white/60 bg-white/50 backdrop-blur-md"}`}
      data-testid={`paysum-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">{label}</div>
      <div className={`mt-1 text-lg font-bold ${highlight ? "text-purple-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ value, label, tone = "slate" }) {
  const toneCls = {
    slate: "text-slate-900",
    rose: "text-rose-600",
    emerald: "text-emerald-700",
  }[tone];
  return (
    <div>
      <div className={`font-display text-2xl font-bold ${toneCls}`}>{value}</div>
      <div className="text-[9px] uppercase text-slate-500 tracking-wider mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function DetailRow({ label, value, empty, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">{label}</div>
      <div className={`text-sm mt-0.5 ${value ? "text-slate-800" : "text-slate-400 italic"} ${mono ? "font-mono tracking-wider" : ""}`}>
        {value || empty}
      </div>
    </div>
  );
}
